import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { getBootstrapStatus } from '../services/auth.service';

const loginSchema = z.object({
  email: z.string().min(1, 'required').email('invalid'),
  password: z.string().min(1, 'required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { loginWithCredentials } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check for session expiration on mount
  useEffect(() => {
    const expired = sessionStorage.getItem('sessionExpired');
    if (expired === 'true') {
      setSessionExpired(true);
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  useEffect(() => {
    const checkBootstrap = async () => {
      try {
        const status = await getBootstrapStatus();
        if (status.requiresActivation) {
          navigate('/activate', { replace: true });
        }
      } catch {
        // Ignore status check failures on login screen
      }
    };

    checkBootstrap();
  }, [navigate]);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError('');
    setIsLoading(true);
    try {
      await loginWithCredentials(data.email, data.password);
    } catch (err) {
      setError(t('common.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
            <LogIn className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {t('pages.login.title')}
          </CardTitle>
          <CardDescription>
            {t('pages.login.subtitle', 'Enter your credentials to access your account')}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleFormSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {sessionExpired && !error && (
              <Alert variant="warning">
                {t('pages.login.sessionExpired', 'Your session has expired. Please log in again.')}
              </Alert>
            )}

            {error && (
              <Alert variant="error">
                {error}
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('pages.login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                autoFocus
                {...register('email')}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-sm text-red-500">
                  {errors.email.type === 'too_small'
                    ? t('pages.login.errors.emailRequired')
                    : t('pages.login.errors.emailInvalid')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('pages.login.password')}</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:underline"
                >
                  {t('pages.login.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                {...register('password')}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-sm text-red-500">
                  {t('pages.login.errors.passwordRequired')}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t('common.loading', 'Loading...')}
                </span>
              ) : (
                t('pages.login.submit')
              )}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              {t('pages.login.noAccount', "Don't have an account?")}{' '}
              <Link
                to="/register"
                className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium hover:underline"
              >
                {t('pages.login.register')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default Login;
